export const BASE_URL = import.meta.env.VITE_API_URL + '/v1'
export const CLOUDFRONT_URL = import.meta.env.VITE_CLOUDFRONT_URL

/**
 * Golf club logo on the CDN. With a size: the square WebP variant the API renders on upload — pick the
 * smallest that covers the slot at 2x (≤64px → 128, ≤128px → 256, larger → 512). Without: the original
 * upload, for print and for viewing the source file.
 */
export const golfclubLogoUrl = (id: number, size?: 128 | 256 | 512) =>
  size
    ? `${CLOUDFRONT_URL}/golfclub/${id}/logo/logo_${id}_${size}.webp`
    : `${CLOUDFRONT_URL}/golfclub/${id}/logo/logo_${id}.png`

/**
 * `@error` handler for a sized logo: retries once with the original PNG, in case the WebP variant is
 * missing. Returns false when there is nothing left to retry, so callers chain their own fallback:
 * `@error="retryLogoAsPng($event) || (logoError = true)"`.
 */
export const retryLogoAsPng = (event: Event) => {
  const img = event.target as HTMLImageElement
  const png = img.src.replace(/_(?:128|256|512)\.webp(\?.*)?$/, '.png$1')
  if (png === img.src) {
    return false
  }
  img.src = png
  return true
}
export const ENV = import.meta.env.VITE_APP_ENV || 'local'

export const PUBLIC_ENDPOINTS = {
  website: {
    partnerLogos: '/website/content/partner/logos',
    partnerDeals: '/website/content/partner/deals',
    contactForm: '/website/form/generic',
    golfclubForm: '/website/form/golfclub',
    helpAdmin: '/website/form/help/admin',
  },
}

export const PRIVATE_ENDPOINTS = {
  auth: {
    register: '/auth/register',
    login: '/auth/login',
  },

  adminUser: {
    list: '/auth/admin/users',
    byId: (id: number) => `/auth/admin/users/${id}`,
  },

  user: {
    create: '/user',
    me: '/user/me',
    all: '/user',
    sendVerificationEmail: '/user/sendVerificationEmail',
    verifyEmail: '/user/verifyEmail',
    update: (id: number) => `/user/${id}`,
    delete: (id: number) => `/user/${id}`,
  },

  club: {
    create: '/club',
    all: '/club',
    statistics: '/club/statistics',
    single: (id: number) => `/club/${id}`,
    geojson: (id: number) => `/club/${id}/geojson`,
    update: (id: number) => `/club/${id}`,
    delete: (id: number) => `/club/${id}`,
    mapPoints: (id: number) => `/club/${id}/map-points`,
  },

  course: {
    create: '/course',
    all: '/course',
    single: (id: number) => `/course/${id}`,
    update: (id: number) => `/course/${id}`,
    delete: (id: number) => `/course/${id}`,
  },

  hole: {
    all: '/hole',
    single: (id: number) => `/hole/${id}`,
    update: (id: number) => `/hole/${id}`,
    geojson: (id: number) => `/hole/${id}/geojson`,
  },

  target: {
    create: '/target',
    all: '/target',
    schedules: '/target/schedules',
    applyLayoutTargetPreset: '/target/apply-layout-target-preset',
    single: (id: number) => `/target/${id}`,
    update: (id: number) => `/target/${id}`,
    delete: (id: number) => `/target/${id}`,
  },

  customer: {
    create: '/customer',
    all: '/customer',
    single: (id: number) => `/customer/${id}`,
    update: (id: number) => `/customer/${id}`,
    delete: (id: number) => `/customer/${id}`,
    createStripeCustomer: (id: number) => `/customer/${id}/stripe`,
    createStripeSubscription: (id: number) => `/customer/${id}/stripe/subscription`,
    stripeInfo: (id: number) => `/customer/${id}/stripe`,
    addManager: (id: number, email: string) => `/customer/${id}/manager/${email}`,
    deleteManager: (id: number, email: string) => `/customer/${id}/manager/${email}`,
  },

  danger: {
    create: '/danger',
    all: '/danger',
    single: (id: number) => `/danger/${id}`,
    update: (id: number) => `/danger/${id}`,
    delete: (id: number) => `/danger/${id}`,
  },

  notification: {
    create: '/notification',
    all: '/notification',
    update: (id: number) => `/notification/${id}`,
    delete: (id: number) => `/notification/${id}`,
  },

  event: {
    create: '/event',
    all: '/event',
    single: (id: number) => `/event/${id}`,
    update: (id: number) => `/event/${id}`,
    delete: (id: number) => `/event/${id}`,
    statsByGolfclub: '/event/stats/by-golfclub',
  },

  topic: {
    create: '/topic',
    all: '/topic',
    single: (id: number) => `/topic/${id}`,
    update: (id: number) => `/topic/${id}`,
    delete: (id: number) => `/topic/${id}`,
  },

  maintenance: {
    active: '/maintenance/active',
    all: '/maintenance',
    single: (id: number) => `/maintenance/${id}`,
    create: '/maintenance',
    update: (id: number) => `/maintenance/${id}`,
    delete: (id: number) => `/maintenance/${id}`,
  },

  device: {
    create: '/device',
    all: '/device',
    single: (id: number) => `/device/${id}`,
    update: (id: number) => `/device/${id}`,
    delete: (id: number) => `/device/${id}`,
  },

  layout: {
    create: '/layout',
    all: '/layout',
    single: (id: number) => `/layout/${id}`,
    update: (id: number) => `/layout/${id}`,
    delete: (id: number) => `/layout/${id}`,
  },

  layout_print: {
    create: '/layout-print',
    all: '/layout-print',
    single: (id: number) => `/layout-print/${id}`,
    update: '/layout-print',
    delete: (id: number) => `/layout-print/${id}`,
  },

  layout_target_preset: {
    create: '/layout-target-preset',
    all: '/layout-target-preset',
    single: (id: number) => `/layout-target-preset/${id}`,
    update: '/layout-target-preset',
    delete: (id: number) => `/layout-target-preset/${id}`,
  },

  tracker: {
    all: '/tracker',
    activeDays: '/tracker/active-days',
  },

  user_tracker: {
    all: '/user/user-tracker',
    readMessage: (id: number) => `/user/user-tracker/${id}/readMessage`,
    sendNotification: '/user/user-tracker/notification',
  },

  adv: {
    create: '/adv',
    all: '/adv',
    count: '/adv/count',
    single: (id: number) => `/adv/${id}`,
    update: (id: number) => `/adv/${id}`,
    delete: (id: number) => `/adv/${id}`,
  },

  advertiser: {
    create: '/advertiser',
    all: '/advertiser',
    count: '/advertiser/count',
    single: (id: number) => `/advertiser/${id}`,
    update: (id: number) => `/advertiser/${id}`,
    delete: (id: number) => `/advertiser/${id}`,
  },

  campaign: {
    create: '/campaign',
    all: '/campaign',
    count: '/campaign/count',
    single: (id: number) => `/campaign/${id}`,
    update: (id: number) => `/campaign/${id}`,
    delete: (id: number) => `/campaign/${id}`,
  },

  stats: {
    golfclubDaily: '/stats/golfclub-daily',
    ranking: '/stats/golfclub-daily/ranking',
  },

  pace: {
    user: '/stats/pace/user',
    tracker: '/stats/pace/tracker',
    course: '/stats/pace/course',
  },

  iotee: {
    sites: '/iotee/site',
    devices: '/iotee/device',
    updateSite: (id: number) => `/iotee/site/${id}`,
    syncSites: '/iotee/site/sync',
    syncDevices: (siteId: string) => `/iotee/site/${siteId}/sync`,
    loadDeviceHistory: (siteId: string, deviceId: string) =>
      `/iotee/site/${siteId}/devices/${deviceId}/history/load`,
  },

  partner: {
    all: '/internal/partner',
    single: (id: number) => `/internal/partner/${id}`,
    create: '/internal/partner',
    update: (id: number) => `/internal/partner/${id}`,
    delete: (id: number) => `/internal/partner/${id}`,
    clubs: '/partner/club',
    singleClub: (id: number) => `/partner/club/${id}`,
    clubGeoJson: (id: number) => `/partner/club/${id}/geojson`,
    courses: '/partner/course',
  },

  leadingCourse: {
    all: '/lc',
    single: (id: number) => `/lc/${id}`,
    create: '/lc',
    update: (id: number) => `/lc/${id}`,
    delete: (id: number) => `/lc/${id}`,
  },

  courseRev: {
    all: '/courserev',
    single: (id: number) => `/courserev/${id}`,
  },

  excel: '/excel',

  file: {
    upload: '/file',
    delete: '/file',
  },
}
